/**
 * Intake API client.
 *
 * Every write goes through the `customer-rfq-submit` function — anonymous
 * visitors have no direct database or storage access, by design. The function
 * issues a draft token on creation, and that token is the only key to the
 * request afterwards.
 *
 * Reference data (regions, insurers, makes/models) is public and read directly.
 */

import { Client, Databases, Functions, ID, Query } from 'appwrite';

const client = new Client()
  .setEndpoint(process.env.REACT_APP_APPWRITE_ENDPOINT || 'http://localhost/v1')
  .setProject(process.env.REACT_APP_APPWRITE_PROJECT || 'dev-flickquote');

export const databases = new Databases(client);
export const functions = new Functions(client);
export { ID, Query };

export const DATABASE_ID = 'flickquote';

export const COLLECTIONS = {
  REGIONS: 'regions',
  INSURERS: 'insurers',
  VEHICLE_MAKES_MODELS: 'vehicle_makes_models'
};

const FUNCTIONS = {
  INTAKE: 'customer-rfq-submit',
  GET_TEAM: 'customer-get-team'
};

// =====================================================
// TRANSPORT
// =====================================================

/**
 * Call a function route and unwrap the `{ success, data | error }` envelope.
 * Throws an Error carrying the server's message, which is written to be shown
 * to the customer as-is for 4xx.
 *
 * Every route is a POST. These are RPC calls through a function, not a REST
 * API, and verbs bought nothing but another thing to go wrong between the SDK,
 * the gateway and the runtime — an update failing while a create succeeded is
 * not a puzzle worth having.
 *
 * The full failure is logged to the console: the customer only ever sees a
 * short message, so without this a support conversation has nothing to go on.
 */
async function call(functionId, path, payload) {
  let execution;
  try {
    execution = await functions.createExecution(
      functionId,
      JSON.stringify(payload || {}),
      false,
      path,
      'POST'
    );
  } catch (err) {
    console.error(`[intake] ${path} could not be reached:`, err);
    throw new Error('We could not reach the server. Please check your connection.');
  }

  let parsed;
  try {
    parsed = JSON.parse(execution.responseBody || '{}');
  } catch {
    console.error(`[intake] ${path} returned an unreadable response:`, {
      status: execution.responseStatusCode,
      body: execution.responseBody?.slice(0, 500)
    });
    throw new Error('The server sent a response we could not read. Please try again.');
  }

  if (!parsed.success) {
    console.error(`[intake] ${path} failed:`, {
      status: execution.responseStatusCode,
      code: parsed.error?.code,
      message: parsed.error?.message
    });

    const error = new Error(parsed.error?.message || 'Something went wrong. Please try again.');
    error.code = parsed.error?.code;
    error.status = execution.responseStatusCode;
    throw error;
  }

  return parsed.data;
}

// =====================================================
// ORGANISATION
// =====================================================

/**
 * Validate the panel shop in the URL and fetch its public details (name and
 * branding) before showing the form.
 */
export async function getOrganisation(teamId) {
  if (!teamId) return { valid: false, error: 'No panel shop specified' };

  try {
    const execution = await functions.createExecution(
      FUNCTIONS.GET_TEAM,
      JSON.stringify({ teamId }),
      false,
      '/',
      'POST'
    );

    const response = JSON.parse(execution.responseBody || '{}');

    if (!response.success) {
      const message = typeof response.error === 'string'
        ? response.error
        : (response.error?.message || 'Panel shop not found');
      return { valid: false, error: message };
    }

    if (!response.team.acceptingQuotes) {
      return {
        valid: false,
        error: 'This panel shop is not currently accepting online quote requests'
      };
    }

    return {
      valid: true,
      organisation: {
        id: response.team.id,
        name: response.team.name,
        branding: response.team.branding || null,
        services: response.team.services || []
      }
    };
  } catch (err) {
    console.error('Team validation failed:', err);
    return { valid: false, error: 'Unable to load panel shop details' };
  }
}

// =====================================================
// DRAFT LIFECYCLE
// =====================================================

/** Create the quote request. Returns the token — store it before anything else. */
export function createDraft({ teamId, customer }) {
  return call(FUNCTIONS.INTAKE, '/draft', { teamId, customer });
}

/** Patch any combination of customer / vehicle / quote request fields. */
export function updateDraft(token, patch) {
  return call(FUNCTIONS.INTAKE, '/draft/update', { token, ...patch });
}

/** Read a request back — used when resuming from a link on another device. */
export function getDraft(token) {
  return call(FUNCTIONS.INTAKE, '/draft/get', { token });
}

export function addImage(token, image) {
  return call(FUNCTIONS.INTAKE, '/draft/images', { token, image });
}

export function deleteImage(token, imageId) {
  return call(FUNCTIONS.INTAKE, '/draft/images/delete', { token, imageId });
}

/** Thumbnail of an already-uploaded photo (the bucket itself is team-only). */
export function getImagePreview(token, imageId) {
  return call(FUNCTIONS.INTAKE, '/draft/images/preview', { token, imageId });
}

export function submitDraft(token, { photosPromised, resumeUrl } = {}) {
  return call(FUNCTIONS.INTAKE, '/draft/submit', { token, photosPromised, resumeUrl });
}

export function sendResumeLink(token, { email, resumeUrl } = {}) {
  return call(FUNCTIONS.INTAKE, '/draft/resume-link', { token, email, resumeUrl });
}

// =====================================================
// REFERENCE DATA
// =====================================================

export async function getInsurers() {
  const response = await databases.listDocuments(DATABASE_ID, COLLECTIONS.INSURERS, [
    Query.equal('active', true),
    Query.orderAsc('name'),
    Query.limit(100)
  ]);

  return response.documents.map((doc) => ({ id: doc.$id, name: doc.name }));
}

export async function getRegions() {
  const response = await databases.listDocuments(DATABASE_ID, COLLECTIONS.REGIONS, [
    Query.orderAsc('region'),
    Query.limit(100)
  ]);

  return response.documents.map((doc) => ({
    id: doc.$id,
    region: doc.region,
    country: doc.country
  }));
}

export async function getVehicleMakesModels() {
  const response = await databases.listDocuments(DATABASE_ID, COLLECTIONS.VEHICLE_MAKES_MODELS, [
    Query.orderAsc('make'),
    Query.limit(5000)
  ]);

  const byMake = {};
  response.documents.forEach(({ make, model }) => {
    if (!make) return;
    if (!byMake[make]) byMake[make] = [];
    if (model && !byMake[make].includes(model)) byMake[make].push(model);
  });

  Object.values(byMake).forEach((models) => models.sort());
  return byMake;
}

export default client;
